/**
 * Capture hours at the point of service, for a qualified site this org runs.
 *
 * Two ways in:
 *  - Staff entry. The staff member filing the line is the organization's
 *    attestation, so it is confirmed on save.
 *  - A photographed paper sign-in sheet. Claude reads the handwriting into
 *    proposed lines; a person checks every line against the photo, edits what is
 *    wrong, and confirms. Nothing is filed on the reading alone, and an unclear
 *    line is highlighted, never dropped.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, CheckCircle2, FileImage, Loader2, PenLine, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { PageState } from '../weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  type ActivityType,
  type MemberHit,
  type SheetBatch,
  type SheetRow,
  type Site,
  todayISO,
  verifyApi,
} from './api';
import { ErrorNote, Field, NativeSelect, activityLabel, errMessage, fmtDate, inputClass } from './shared';

type Mode = 'staff' | 'sheet';

export function VerifyCapture({ onNavigate }: { onNavigate: (tab: string) => void }) {
  const { t } = useAppTranslation();
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>('staff');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    verifyApi
      .sites()
      .then((r) => {
        const mine = r.sites.filter((s) => s.can_capture);
        setSites(mine);
        setSiteId((cur) => cur ?? mine[0]?.id ?? null);
      })
      .catch((e) => setError(errMessage(e, t('verify.capture.load_failed', 'Could not load your sites.'))))
      .finally(() => setLoading(false));
  }, [t]);

  const site = sites.find((s) => s.id === siteId) || null;

  if (loading) return <PageState variant="loading" />;
  if (error) return <ErrorNote message={error} />;
  if (sites.length === 0) {
    return (
      <PageState
        variant="empty"
        title={t('verify.capture.no_sites_title', 'No qualified site to record at')}
        message={t(
          'verify.capture.no_sites',
          'Hours are recorded at a qualified organization your team runs. Add it to the registry and qualify it first.',
        )}
        action={
          <Button size="sm" onClick={() => onNavigate('registry')}>
            {t('verify.capture.go_registry', 'Open the registry')}
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('verify.capture.site', 'Site')} className="min-w-[260px]">
          <NativeSelect value={siteId ?? ''} onChange={(e) => setSiteId(Number(e.target.value))}>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <div className="inline-flex rounded-md border border-v2border bg-surface p-0.5">
          {(
            [
              ['staff', PenLine, t('verify.capture.mode_staff', 'Staff entry')],
              ['sheet', Camera, t('verify.capture.mode_sheet', 'Paper sign-in sheet')],
            ] as const
          ).map(([key, Icon, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              className={`inline-flex items-center gap-2 rounded px-3 py-1.5 text-sm ${
                mode === key ? 'bg-v2accent-soft text-v2accent-ink' : 'text-ink-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {site && mode === 'staff' && <StaffEntry key={site.id} site={site} />}
      {site && mode === 'sheet' && <SheetCapture key={site.id} site={site} onNavigate={onNavigate} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff entry
// ---------------------------------------------------------------------------

function StaffEntry({ site }: { site: Site }) {
  const { t } = useAppTranslation();
  const blank = useMemo(
    () => ({
      first: '',
      last: '',
      dob: '',
      phone: '',
      activity_type: (site.activity_types[0] || '') as ActivityType | '',
      service: '',
      date: todayISO(),
      hours: '',
      poc: site.contact_name || '',
    }),
    [site],
  );
  const [form, setForm] = useState(blank);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<MemberHit[]>([]);
  const [member, setMember] = useState<MemberHit | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    if (!query.trim()) {
      setHits([]);
      return;
    }
    const h = window.setTimeout(() => {
      verifyApi
        .members(query)
        .then((r) => setHits(r.members))
        .catch(() => setHits([]));
    }, 250);
    return () => window.clearTimeout(h);
  }, [query]);

  const set = (k: keyof typeof blank, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const hours = Number(form.hours);
  const named = member || (form.first.trim() && form.last.trim());
  const valid = named && form.activity_type && form.service.trim().length >= 2 && form.date && hours > 0 && hours <= 24;

  const submit = async () => {
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const rec = await verifyApi.createRecord({
        site_id: site.id,
        member_id: member?.id,
        participant_first_name: member ? undefined : form.first.trim(),
        participant_last_name: member ? undefined : form.last.trim(),
        participant_dob: member ? undefined : form.dob || undefined,
        participant_phone: member ? undefined : form.phone || undefined,
        activity_type: form.activity_type,
        service_description: form.service.trim(),
        activity_date: form.date,
        hours,
        point_of_contact_name: form.poc.trim() || undefined,
      });
      setSaved(
        t('verify.capture.saved', 'Filed and confirmed: {{hours}} h for {{name}} on {{date}}.', {
          hours: rec.hours,
          name: `${rec.participant_first_name} ${rec.participant_last_name}`,
          date: fmtDate(rec.activity_date),
        }),
      );
      setForm({ ...blank, activity_type: form.activity_type, service: form.service, date: form.date });
      setMember(null);
      setQuery('');
    } catch (e) {
      setError(errMessage(e, t('verify.capture.save_failed', 'Could not file the line.')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('verify.capture.staff_title', 'Record hours')}</CardTitle>
        <p className="text-sm text-ink-500">
          {t(
            'verify.capture.staff_desc',
            'You are filing this as {{site}}. Your name goes on the line as the person who confirmed it.',
            { site: site.name },
          )}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Field
            label={t('verify.capture.who', 'Who')}
            hint={t(
              'verify.capture.who_hint',
              'Pick one of your members, or type the name and date of birth of someone who is not. The date of birth is what matches them to their Medicaid enrollment.',
            )}
          >
            {member ? (
              <div className="flex items-center justify-between rounded-md border border-v2border bg-paper-2 px-3 py-2 text-sm">
                <span className="text-ink">
                  {member.first_name} {member.last_name}
                  {!member.has_dob && (
                    <span className="ml-2 text-xs text-attention-ink">
                      {t('verify.capture.no_dob', 'no date of birth on file')}
                    </span>
                  )}
                </span>
                <Button size="sm" variant="ghost" onClick={() => setMember(null)}>
                  {t('verify.change', 'Change')}
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-ink-400" />
                <input
                  className={`${inputClass} pl-9`}
                  placeholder={t('verify.capture.search_member', 'Search your members…')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {hits.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-v2border bg-surface shadow-v2-1">
                    {hits.map((h) => (
                      <button
                        key={h.id}
                        type="button"
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-paper-2"
                        onClick={() => {
                          setMember(h);
                          setHits([]);
                        }}
                      >
                        {h.first_name} {h.last_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Field>
          {!member && (
            <div className="grid gap-3 sm:grid-cols-4">
              <input
                className={inputClass}
                placeholder={t('verify.first_name', 'First name')}
                value={form.first}
                onChange={(e) => set('first', e.target.value)}
              />
              <input
                className={inputClass}
                placeholder={t('verify.last_name', 'Last name')}
                value={form.last}
                onChange={(e) => set('last', e.target.value)}
              />
              <input
                className={inputClass}
                type="date"
                aria-label={t('verify.dob', 'Date of birth')}
                value={form.dob}
                max={todayISO()}
                onChange={(e) => set('dob', e.target.value)}
              />
              <input
                className={inputClass}
                placeholder={t('verify.phone_optional', 'Phone (optional)')}
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t('verify.activity_type', 'Activity type')}>
            <NativeSelect value={form.activity_type} onChange={(e) => set('activity_type', e.target.value)}>
              {site.activity_types.map((a) => (
                <option key={a} value={a}>
                  {activityLabel(t, a)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label={t('verify.service', 'Type of service')} className="lg:col-span-1">
            <input
              className={inputClass}
              placeholder={t('verify.service_ph', 'e.g. Food pantry sorting')}
              value={form.service}
              onChange={(e) => set('service', e.target.value)}
            />
          </Field>
          <Field label={t('verify.date', 'Date')}>
            <input className={inputClass} type="date" max={todayISO()} value={form.date} onChange={(e) => set('date', e.target.value)} />
          </Field>
          <Field label={t('verify.hours', 'Hours')}>
            <input
              className={inputClass}
              type="number"
              min={0.25}
              max={24}
              step={0.25}
              value={form.hours}
              onChange={(e) => set('hours', e.target.value)}
            />
          </Field>
          <Field label={t('verify.poc', 'Point of contact')} className="sm:col-span-2">
            <input className={inputClass} value={form.poc} onChange={(e) => set('poc', e.target.value)} />
          </Field>
        </div>

        <ErrorNote message={error} />
        {saved && (
          <div className="flex items-center gap-2 rounded-md bg-success-soft px-3 py-2 text-sm text-success-ink">
            <CheckCircle2 className="h-4 w-4" />
            {saved}
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={() => void submit()} disabled={!valid || saving}>
            {saving ? t('verify.saving', 'Saving…') : t('verify.capture.file_confirm', 'File and confirm')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Paper sign-in sheet
// ---------------------------------------------------------------------------

interface EditableRow {
  include: boolean;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone: string;
  activity_date: string;
  hours: string;
  activity_type: string;
  service_description: string;
  confidence: number;
  needs_review: boolean;
  unclear: string | null;
  signature_present: boolean;
}

function toEditable(r: SheetRow, site: Site, sheetActivity: string | null | undefined): EditableRow {
  const onlyType = site.activity_types.length === 1 ? site.activity_types[0] : '';
  const type = r.activity_type && site.activity_types.includes(r.activity_type) ? r.activity_type : onlyType;
  return {
    include: true,
    first_name: r.first_name || '',
    last_name: r.last_name || '',
    date_of_birth: r.date_of_birth || '',
    phone: r.phone || '',
    activity_date: r.activity_date || '',
    hours: r.hours ? String(r.hours) : '',
    activity_type: type,
    service_description: r.service_description || sheetActivity || '',
    confidence: r.confidence,
    needs_review: r.needs_review,
    unclear: r.unclear,
    signature_present: r.signature_present,
  };
}

function rowProblems(r: EditableRow): string[] {
  const p: string[] = [];
  if (!r.first_name.trim() || !r.last_name.trim()) p.push('name');
  if (!r.activity_date) p.push('date');
  const h = Number(r.hours);
  if (!(h > 0 && h <= 24)) p.push('hours');
  if (!r.activity_type) p.push('activity');
  if (r.service_description.trim().length < 2) p.push('service');
  return p;
}

function SheetCapture({ site, onNavigate }: { site: Site; onNavigate: (tab: string) => void }) {
  const { t } = useAppTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [batch, setBatch] = useState<SheetBatch | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [poc, setPoc] = useState(site.contact_name || '');
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);
  const [recent, setRecent] = useState<{ id: number; site_id: number; status: string; created_at: string; row_count: number }[]>([]);

  const loadRecent = useCallback(() => {
    verifyApi
      .sheets()
      .then((r) => setRecent(r.sheets.filter((s) => s.site_id === site.id)))
      .catch(() => setRecent([]));
  }, [site.id]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  useEffect(
    () => () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    },
    [imageUrl],
  );

  const openBatch = async (b: SheetBatch) => {
    setBatch(b);
    setDone(null);
    setRows((b.extraction.rows || []).map((r) => toEditable(r, site, b.extraction.sheet?.activity_seen)));
    setPoc(b.extraction.sheet?.supervisor_seen || site.contact_name || '');
    try {
      const url = await verifyApi.sheetImage(b.id);
      setImageUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return url;
      });
    } catch {
      setImageUrl(null);
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    setDone(null);
    try {
      await openBatch(await verifyApi.uploadSheet(site.id, file));
      loadRecent();
    } catch (e) {
      setError(errMessage(e, t('verify.sheet.upload_failed', 'Could not read that photo.')));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const update = (i: number, patch: Partial<EditableRow>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      {
        include: true,
        first_name: '',
        last_name: '',
        date_of_birth: '',
        phone: '',
        activity_date: batch?.extraction.sheet?.date_seen || todayISO(),
        hours: '',
        activity_type: site.activity_types.length === 1 ? site.activity_types[0] : '',
        service_description: batch?.extraction.sheet?.activity_seen || '',
        confidence: 1,
        needs_review: false,
        unclear: null,
        signature_present: false,
      },
    ]);

  const included = rows.filter((r) => r.include);
  const blocked = included.some((r) => rowProblems(r).length > 0);

  const confirm = async () => {
    if (!batch) return;
    setConfirming(true);
    setError(null);
    try {
      const res = await verifyApi.confirmSheet(batch.id, {
        point_of_contact_name: poc.trim() || undefined,
        rows: included.map((r) => ({
          first_name: r.first_name.trim(),
          last_name: r.last_name.trim(),
          date_of_birth: r.date_of_birth || null,
          phone: r.phone || null,
          activity_date: r.activity_date,
          hours: Number(r.hours),
          activity_type: r.activity_type,
          service_description: r.service_description.trim(),
          confidence: r.confidence,
        })),
      });
      setDone(res.confirmed);
      setBatch(res.batch);
      loadRecent();
    } catch (e) {
      setError(errMessage(e, t('verify.sheet.confirm_failed', 'Could not confirm the sheet.')));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
          <div className="max-w-xl text-sm text-ink-700">
            <div className="font-medium text-ink">{t('verify.sheet.title', 'Photograph the paper sign-in sheet')}</div>
            <p className="mt-1 text-ink-500">
              {t(
                'verify.sheet.desc',
                'We read the handwriting into proposed lines. Nothing is filed until you check each line against the photo and confirm. Unclear lines are highlighted, never dropped.',
              )}
            </p>
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f);
              }}
            />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
              {uploading ? t('verify.sheet.reading', 'Reading the sheet…') : t('verify.sheet.upload', 'Upload a photo')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <ErrorNote message={error} />

      {!batch && recent.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('verify.sheet.recent', 'Recent sheets at this site')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {recent.slice(0, 8).map((s) => (
              <button
                key={s.id}
                type="button"
                className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-paper-2"
                onClick={async () => {
                  try {
                    await openBatch(await verifyApi.sheet(s.id));
                  } catch (e) {
                    setError(errMessage(e, t('verify.sheet.open_failed', 'Could not open that sheet.')));
                  }
                }}
              >
                <span className="flex items-center gap-2 text-ink">
                  <FileImage className="h-4 w-4 text-ink-400" />
                  {fmtDate(s.created_at)} · {t('verify.sheet.n_lines', '{{n}} lines', { n: s.row_count })}
                </span>
                <span className="text-xs text-ink-500">{s.status}</span>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {batch && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <Card className="overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('verify.sheet.photo', 'The photo')}</CardTitle>
              {batch.extraction.sheet && (
                <p className="text-xs text-ink-500">
                  {[
                    batch.extraction.sheet.organization_seen,
                    batch.extraction.sheet.activity_seen,
                    batch.extraction.sheet.date_seen && fmtDate(batch.extraction.sheet.date_seen),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {imageUrl ? (
                <a href={imageUrl} target="_blank" rel="noreferrer">
                  <img src={imageUrl} alt="" className="w-full rounded border border-v2border" />
                </a>
              ) : (
                <div className="text-sm text-ink-500">{t('verify.sheet.no_photo', 'Photo unavailable.')}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                {batch.status === 'confirmed'
                  ? t('verify.sheet.confirmed_title', 'Confirmed')
                  : t('verify.sheet.review_title', 'Check each line, then confirm')}
              </CardTitle>
              {batch.status === 'failed' && (
                <p className="text-sm text-attention-ink">
                  {t(
                    'verify.sheet.failed',
                    'We could not read this photo. Add the lines by hand below, or try a sharper photo.',
                  )}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {done !== null || batch.status === 'confirmed' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 rounded-md bg-success-soft px-3 py-2 text-sm text-success-ink">
                    <CheckCircle2 className="h-4 w-4" />
                    {done !== null
                      ? t('verify.sheet.done', '{{n}} lines filed and confirmed.', { n: done })
                      : t('verify.sheet.already', 'This sheet was confirmed earlier.')}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setBatch(null);
                        setRows([]);
                        setDone(null);
                      }}
                    >
                      {t('verify.sheet.another', 'Another sheet')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onNavigate('records')}>
                      {t('verify.sheet.see_records', 'See records')}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {rows.map((r, i) => {
                      const problems = r.include ? rowProblems(r) : [];
                      return (
                        <div
                          key={i}
                          className={`rounded-md border p-2 ${
                            !r.include
                              ? 'border-v2border opacity-50'
                              : r.needs_review || problems.length
                                ? 'border-attention bg-attention-soft'
                                : 'border-v2border'
                          }`}
                        >
                          <div className="mb-2 flex items-center justify-between text-xs text-ink-500">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={r.include}
                                onChange={(e) => update(i, { include: e.target.checked })}
                              />
                              {t('verify.sheet.line', 'Line {{n}}', { n: i + 1 })}
                            </label>
                            <span>
                              {r.needs_review && t('verify.sheet.check_this', 'Check this line · ')}
                              {t('verify.sheet.confidence', 'read confidence {{pct}}%', {
                                pct: Math.round(r.confidence * 100),
                              })}
                              {!r.signature_present && ` · ${t('verify.sheet.no_signature', 'no signature seen')}`}
                            </span>
                          </div>
                          {r.unclear && <div className="mb-2 text-xs text-attention-ink">{r.unclear}</div>}
                          <div className="grid gap-2 sm:grid-cols-6">
                            <input
                              className={inputClass}
                              placeholder={t('verify.first_name', 'First name')}
                              value={r.first_name}
                              onChange={(e) => update(i, { first_name: e.target.value })}
                            />
                            <input
                              className={inputClass}
                              placeholder={t('verify.last_name', 'Last name')}
                              value={r.last_name}
                              onChange={(e) => update(i, { last_name: e.target.value })}
                            />
                            <input
                              className={inputClass}
                              type="date"
                              title={t('verify.dob', 'Date of birth')}
                              value={r.date_of_birth}
                              onChange={(e) => update(i, { date_of_birth: e.target.value })}
                            />
                            <input
                              className={inputClass}
                              type="date"
                              title={t('verify.date', 'Date')}
                              max={todayISO()}
                              value={r.activity_date}
                              onChange={(e) => update(i, { activity_date: e.target.value })}
                            />
                            <input
                              className={inputClass}
                              type="number"
                              min={0.25}
                              max={24}
                              step={0.25}
                              placeholder={t('verify.hours', 'Hours')}
                              value={r.hours}
                              onChange={(e) => update(i, { hours: e.target.value })}
                            />
                            <NativeSelect
                              value={r.activity_type}
                              onChange={(e) => update(i, { activity_type: e.target.value })}
                            >
                              <option value="">{t('verify.activity_type', 'Activity type')}</option>
                              {site.activity_types.map((a) => (
                                <option key={a} value={a}>
                                  {activityLabel(t, a)}
                                </option>
                              ))}
                            </NativeSelect>
                            <input
                              className={`${inputClass} sm:col-span-6`}
                              placeholder={t('verify.service', 'Type of service')}
                              value={r.service_description}
                              onChange={(e) => update(i, { service_description: e.target.value })}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {rows.length === 0 && (
                      <p className="text-sm text-ink-500">{t('verify.sheet.no_rows', 'No lines were read from this photo.')}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={addRow}>
                    {t('verify.sheet.add_line', '+ Add a line by hand')}
                  </Button>
                  <Field label={t('verify.sheet.poc', 'Point of contact for these lines')}>
                    <input className={inputClass} value={poc} onChange={(e) => setPoc(e.target.value)} />
                  </Field>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-v2border pt-3">
                    <span className="text-xs text-ink-500">
                      {blocked
                        ? t('verify.sheet.fix_first', 'Highlighted lines need a name, date, hours, activity and service before you confirm.')
                        : t('verify.sheet.you_confirm', 'Confirming files these lines under your name.')}
                    </span>
                    <Button onClick={() => void confirm()} disabled={confirming || blocked || included.length === 0}>
                      {confirming
                        ? t('verify.saving', 'Saving…')
                        : t('verify.sheet.confirm_n', 'Confirm {{n}} lines', { n: included.length })}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
